"use client";

import {
  type AcquisitionDemoFramePhase,
  getTrustedAcquisitionDemoFrameResizeMessage,
} from "@/lib/acquisition-demo/frame-bridge";
import { useEffect, useMemo, useRef, useState } from "react";

export type SpeakingDemoIframeProps = {
  defaultInstructionLanguageCode?: string | null;
  defaultTargetLanguageCode?: string | null;
  experimentVariant?: string;
  loading?: "eager" | "lazy";
  onLoad?: () => void;
  pageFamily: string;
  pagePath: string;
  placement?: string;
  requireTargetLanguageSelection?: boolean;
  scenarioId?: string;
  title?: string;
};

export function SpeakingDemoIframe({
  defaultInstructionLanguageCode,
  defaultTargetLanguageCode,
  experimentVariant,
  loading = "lazy",
  onLoad,
  pageFamily,
  pagePath,
  placement = "embedded_demo",
  requireTargetLanguageSelection = false,
  scenarioId,
  title = "Interactive ChickyTutor live speaking demo",
}: SpeakingDemoIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [embedFrame, setEmbedFrame] = useState<{
    height: number;
    phase: AcquisitionDemoFramePhase;
  } | null>(null);

  const src = useMemo(() => {
    const embedParams = new URLSearchParams({
      sourceFamily: pageFamily,
      sourcePath: pagePath,
      placement,
    });
    if (experimentVariant) embedParams.set("experimentVariant", experimentVariant);
    if (defaultTargetLanguageCode) embedParams.set("target", defaultTargetLanguageCode);
    if (defaultInstructionLanguageCode) {
      embedParams.set("instruction", defaultInstructionLanguageCode);
    }
    if (scenarioId) embedParams.set("scenario", scenarioId);
    if (requireTargetLanguageSelection) embedParams.set("requireTargetSelection", "true");

    return `/embed/speaking-demo?${embedParams.toString()}`;
  }, [
    defaultInstructionLanguageCode,
    defaultTargetLanguageCode,
    experimentVariant,
    pageFamily,
    pagePath,
    placement,
    requireTargetLanguageSelection,
    scenarioId,
  ]);

  useEffect(() => {
    setEmbedFrame(null);
  }, [src]);

  useEffect(() => {
    const handleFrameMessage = (event: MessageEvent) => {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return;

      const message = getTrustedAcquisitionDemoFrameResizeMessage(
        event,
        window.location.origin,
        iframeWindow
      );
      if (!message) return;

      setEmbedFrame((current) => {
        const height =
          message.phase === "active"
            ? Math.max(current?.height || 0, message.height)
            : message.height;
        if (current?.height === height && current.phase === message.phase) return current;
        return { height, phase: message.phase };
      });
    };

    window.addEventListener("message", handleFrameMessage);
    return () => window.removeEventListener("message", handleFrameMessage);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title={title}
      allow="microphone; autoplay; fullscreen; screen-wake-lock"
      allowFullScreen
      loading={loading}
      onLoad={onLoad}
      className="h-[720px] w-full border-0 transition-[height] duration-300 sm:h-[680px]"
      data-frame-phase={embedFrame?.phase}
      style={embedFrame ? { height: `${embedFrame.height}px` } : undefined}
    />
  );
}
